import { useState, forwardRef, useImperativeHandle } from 'react';
import { Section } from '@/components/Section';
import { Button } from 'alurkerja-ui';
import { FieldValues, useForm } from 'react-hook-form';
import { CiSearch } from 'react-icons/ci';
import { MdOutlineRefresh } from 'react-icons/md';
import { FormContainer } from '@/components/form/FormContainer';
import { InputField } from '@/components/form/fields/InputField';
import {
  ISelectOption,
  SelectField,
} from '@/components/form/fields/SelectField';
import { TextAreaField } from '@/components/form/fields/TextAreaField';
import { SelectMultipleField } from '@/components/form/fields/SelectMultipleField';
import { DateRange } from '@/components/form/fields/DateRange';

export interface IFilterSpec {
  name: string;
  label: string;
  type: 'text' | 'date' | 'option' | 'number' | 'textarea' | 'multiOption' | 'daterange';
  options?: ISelectOption[];
  placeholder?: string;
  step?: number;
}

interface TableFilterPanelProps {
  filterSpecs: IFilterSpec[];
  onSubmit: (filter: FieldValues) => void;
}

export type TableFilterPanelRef = {
  toggle: () => void;
};

export const TableFilterPanel = forwardRef<
  TableFilterPanelRef,
  TableFilterPanelProps
>((props, ref) => {
  const form = useForm();
  const [reload, setReload] = useState(1);
  const [hide, setHide] = useState(true);

  useImperativeHandle(ref, () => ({
    toggle: () => setHide(prevState => !prevState),
  }));

  const buildInput = (spec: IFilterSpec) => {
    if (spec.type === 'text') {
      return <InputField {...spec} type="text" />;
    } else if (spec.type === 'textarea') {
      return <TextAreaField {...spec} />;
    } else if (spec.type === 'date') {
      return <InputField {...spec} type="date" />;
    } else if (spec.type === 'option') {
      return <SelectField {...spec} isSearchable />;
    } else if (spec.type === 'number') {
      return <InputField {...spec} type="number" step={spec.step ?? 1} />;
    } else if (spec.type === 'multiOption') {
      return <SelectMultipleField {...spec} isSearchable />;
    } else if (spec.type === 'daterange') {
      return <DateRange {...spec} />;
    }

    return <></>;
  };

  if (hide) {
    return <></>;
  }

  return (
    <div className="pt-4 my-5 border rounded-md mx-7">
      <Section title="Filter Pencarian">
        <div
          onKeyDown={e => {
            // Check if the active element is a textarea
            if (document.activeElement?.tagName.toLowerCase() === 'textarea') {
              return;
            }
            if (e.code === 'Enter') {
              e.preventDefault();
              form.handleSubmit(props.onSubmit)();
            }
          }}
        >
          <FormContainer form={form}>
            <div key={reload} className="grid grid-cols-2 gap-2 gap-x-5">
              {props.filterSpecs.map(criteria => (
                <div key={criteria.name}>{buildInput(criteria)}</div>
              ))}
            </div>

            <div className="flex items-center col-span-2 gap-2 mt-5">
              <Button
                type="button"
                icon={<MdOutlineRefresh />}
                variant="outlined"
                onClick={() => {
                  form.reset({});
                  props.onSubmit({});

                  setReload(prev => prev + 1);
                }}
              >
                Reset
              </Button>

              <Button
                icon={<CiSearch />}
                type="button"
                onClick={form.handleSubmit(props.onSubmit)}
              >
                Cari
              </Button>
            </div>
          </FormContainer>
        </div>
      </Section>
    </div>
  );
});
